'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import SafeImage from '@/components/SafeImage'

type PublicProjectRow = {
  id: string
  title: string
  description: string | null
  title_en: string | null
  description_en: string | null
  category: string
  slug: string
  image_url: string | null
  goal_amount: number | null
  goal_eur: number | null
  currency: string | null
  deadline: string | null
  status: string | null
  project_number: number | null
  sort_order: number | null
  raised_eur: number | null
  progress_pct: number | null
}

interface HomeClientProps {
  projects: PublicProjectRow[]
}

type Lang = 'en' | 'de' | 'nl' | 'pt' | 'fr' | 'es' | 'tw' | 'ig'

const strings: Record<Lang, Record<string, string>> = {
  en: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Current Projects and Funding Status',
    login: 'Login',
    donateNow: 'Donate Now',
    donateNowDesc: 'Support our projects with a donation of €20 and help us create positive change.',
    donate20: 'Donate €20',
    raffleTitle: 'Raffle',
    rafflePrize: 'Win an Off-Grid Property with 35m² Livable House in Portugal!',
    raffleDesc: 'Only 300 meters from Camp Calma. Learn more about this unique opportunity.',
    learnMore: 'Learn More',
    activeProjects: 'Our Projects',
    noActiveProjects: 'No active projects available at the moment.',
    fundingGoal: 'Funding Goal:',
    alreadyFunded: 'Already Funded:',
    deadline: 'Deadline:',
    progress: 'Progress',
    active: 'active',
    footer: 'All rights reserved.',
    learnMoreAbout: 'Learn more about Sankofa Living & Learning and the Sankofa Social digital ecosystem:',
    campCalmaProject: 'Camp Calma Project',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'All essential links for the digital ecosystem Sankofa Social in one compact view.',
    selectLanguage: 'Select Your Language',
    selectLanguageDesc: 'Please choose your preferred language to continue',
  },
  de: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Aktuelle Projekte und Finanzierungsstatus',
    login: 'Anmelden',
    donateNow: 'Jetzt Spenden',
    donateNowDesc: 'Unterstütze unsere Projekte mit einer Spende von 20 € und hilf uns, positive Veränderungen zu schaffen.',
    donate20: '20 € Spenden',
    raffleTitle: 'Gewinnspiel',
    rafflePrize: 'Gewinne ein Off-Grid Grundstück mit 35m² bewohnbarem Haus in Portugal!',
    raffleDesc: 'Nur 300 Meter von Camp Calma entfernt. Erfahre mehr über diese einzigartige Chance.',
    learnMore: 'Mehr erfahren',
    activeProjects: 'Unsere Projekte',
    noActiveProjects: 'Derzeit keine aktiven Projekte verfügbar.',
    fundingGoal: 'Finanzierungsziel:',
    alreadyFunded: 'Bereits finanziert:',
    deadline: 'Deadline:',
    progress: 'Fortschritt',
    active: 'aktiv',
    footer: 'Alle Rechte vorbehalten.',
    learnMoreAbout: 'Erfahre mehr über Sankofa Living & Learning und das digitale Ökosystem Sankofa Social:',
    campCalmaProject: 'Camp Calma Projekt',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Alle wichtigen Links für das digitale Ökosystem Sankofa Social in einer kompakten Ansicht.',
    selectLanguage: 'Wähle deine Sprache',
    selectLanguageDesc: 'Bitte wähle deine bevorzugte Sprache um fortzufahren',
  },
  nl: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Huidige Projecten en Financieringsstatus',
    login: 'Inloggen',
    donateNow: 'Doneer Nu',
    donateNowDesc: 'Ondersteun onze projecten met een donatie van €20 en help ons positieve verandering te creëren.',
    donate20: '€20 Doneren',
    raffleTitle: 'Loterij',
    rafflePrize: 'Win een Off-Grid Eigendom met 35m² Bewoonbaar Huis in Portugal!',
    raffleDesc: 'Slechts 300 meter van Camp Calma. Leer meer over deze unieke kans.',
    learnMore: 'Meer Informatie',
    activeProjects: 'Onze Projecten',
    noActiveProjects: 'Momenteel geen actieve projecten beschikbaar.',
    fundingGoal: 'Financieringsdoel:',
    alreadyFunded: 'Reeds Gefinancierd:',
    deadline: 'Deadline:',
    progress: 'Voortgang',
    active: 'actief',
    footer: 'Alle rechten voorbehouden.',
    learnMoreAbout: 'Leer meer over Sankofa Living & Learning en het digitale ecosysteem Sankofa Social:',
    campCalmaProject: 'Camp Calma Project',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Alle essentiële links voor het digitale ecosysteem Sankofa Social in één compact overzicht.',
    selectLanguage: 'Selecteer uw taal',
    selectLanguageDesc: 'Kies uw voorkeurstaal om verder te gaan',
  },
  pt: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Projetos Atuais e Estado de Financiamento',
    login: 'Entrar',
    donateNow: 'Doar Agora',
    donateNowDesc: 'Apoie nossos projetos com uma doação de €20 e ajude-nos a criar mudanças positivas.',
    donate20: 'Doar €20',
    raffleTitle: 'Sorteio',
    rafflePrize: 'Ganhe uma Propriedade Off-Grid com Casa de 35m² Habitável em Portugal!',
    raffleDesc: 'Apenas 300 metros de Camp Calma. Saiba mais sobre esta oportunidade única.',
    learnMore: 'Saiba Mais',
    activeProjects: 'Nossos Projetos',
    noActiveProjects: 'Nenhum projeto ativo disponível no momento.',
    fundingGoal: 'Meta de Financiamento:',
    alreadyFunded: 'Já Financiado:',
    deadline: 'Prazo Final:',
    progress: 'Progresso',
    active: 'ativo',
    footer: 'Todos os direitos reservados.',
    learnMoreAbout: 'Saiba mais sobre Sankofa Living & Learning e o ecossistema digital Sankofa Social:',
    campCalmaProject: 'Projeto Camp Calma',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Todos os links essenciais para o ecossistema digital Sankofa Social em uma visão compacta.',
    selectLanguage: 'Selecione seu idioma',
    selectLanguageDesc: 'Por favor, escolha seu idioma preferido para continuar',
  },
  fr: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Projets Actuels et État de Financement',
    login: 'Connexion',
    donateNow: 'Faire un Don',
    donateNowDesc: 'Soutenez nos projets avec un don de 20 € et aidez-nous à créer un changement positif.',
    donate20: 'Donner 20 €',
    raffleTitle: 'Tombola',
    rafflePrize: 'Gagnez une Propriété Hors Réseau avec une Maison Habitable de 35m² au Portugal!',
    raffleDesc: 'À seulement 300 mètres de Camp Calma. En savoir plus sur cette opportunité unique.',
    learnMore: 'En Savoir Plus',
    activeProjects: 'Nos Projets',
    noActiveProjects: 'Aucun projet actif disponible pour le moment.',
    fundingGoal: 'Objectif de Financement:',
    alreadyFunded: 'Déjà Financé:',
    deadline: 'Date Limite:',
    progress: 'Progrès',
    active: 'actif',
    footer: 'Tous droits réservés.',
    learnMoreAbout: 'En savoir plus sur Sankofa Living & Learning et l\'écosystème numérique Sankofa Social:',
    campCalmaProject: 'Projet Camp Calma',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Tous les liens essentiels pour l\'écosystème numérique Sankofa Social dans une vue compacte.',
    selectLanguage: 'Sélectionnez votre langue',
    selectLanguageDesc: 'Veuillez choisir votre langue préférée pour continuer',
  },
  es: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Proyectos Actuales y Estado de Financiación',
    login: 'Iniciar Sesión',
    donateNow: 'Donar Ahora',
    donateNowDesc: 'Apoya nuestros proyectos con una donación de €20 y ayúdanos a crear un cambio positivo.',
    donate20: 'Donar €20',
    raffleTitle: 'Sorteo',
    rafflePrize: '¡Gana una Propiedad Off-Grid con Casa Habitable de 35m² en Portugal!',
    raffleDesc: 'A solo 300 metros de Camp Calma. Conoce más sobre esta oportunidad única.',
    learnMore: 'Más Información',
    activeProjects: 'Nuestros Proyectos',
    noActiveProjects: 'No hay proyectos activos disponibles en este momento.',
    fundingGoal: 'Meta de Financiación:',
    alreadyFunded: 'Ya Financiado:',
    deadline: 'Fecha Límite:',
    progress: 'Progreso',
    active: 'activo',
    footer: 'Todos los derechos reservados.',
    learnMoreAbout: 'Más información sobre Sankofa Living & Learning y el ecosistema digital Sankofa Social:',
    campCalmaProject: 'Proyecto Camp Calma',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Todos los enlaces esenciales para el ecosistema digital Sankofa Social en una vista compacta.',
    selectLanguage: 'Seleccione su idioma',
    selectLanguageDesc: 'Por favor, elija su idioma preferido para continuar',
  },
  tw: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Nnwuma a ɛrekɔ so ne Sika a wɔaboa',
    login: 'Kɔ mu',
    donateNow: 'Ma Akyɛdeɛ Seesei',
    donateNowDesc: 'Boa yɛn nnwuma denam €20 akyɛdeɛ so na boa yɛn ma yɛnyɛ nsakraeɛ papa.',
    donate20: 'Ma €20',
    raffleTitle: 'Ntoboa',
    rafflePrize: 'Nya Off-Grid Asase ne 35m² Dan a Wobetumi Atena mu wɔ Portugal!',
    raffleDesc: 'Ɛwɔ Camp Calma nkyɛn 300 meters pɛ. Sua ho nsɛm pii.',
    learnMore: 'Sua Pii',
    activeProjects: 'Yɛn Nnwuma',
    noActiveProjects: 'Nnwuma biara nni hɔ seesei.',
    fundingGoal: 'Sika Botaeɛ:',
    alreadyFunded: 'Sika a Wɔaboa Dada:',
    deadline: 'Berɛ a Ɛbɛba:',
    progress: 'Nkɔsoɔ',
    active: 'ɛrekɔ so',
    footer: 'Nkyedeɛ nyinaa wɔ hɔ.',
    learnMoreAbout: 'Sua ho nsɛm pii wɔ Sankofa Living & Learning ne Sankofa Social digital ecosystem ho:',
    campCalmaProject: 'Camp Calma Nnwuma',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Links a ɛho hia nyinaa wɔ Sankofa Social digital ecosystem mu wɔ hwɛbea tiawa mu.',
    selectLanguage: 'Yi wo kasa',
    selectLanguageDesc: 'Yi kasa a wopɛ na kɔ so',
  },
  ig: {
    mainTitle: 'Sankofa Living & Learning',
    mainSubtitle: 'Ọrụ Ndị Na-aga N\'ihu na Ọnọdụ Ego',
    login: 'Banye',
    donateNow: 'Nye Onyinye Ugbu a',
    donateNowDesc: 'Kwado ọrụ anyị site na onyinye €20 ma nyere anyị aka mee mgbanwe dị mma.',
    donate20: 'Nye €20',
    raffleTitle: 'Nzọ',
    rafflePrize: 'Nweta Ala Off-Grid nwere Ụlọ 35m² Obibi na Portugal!',
    raffleDesc: 'Naanị 300 mita site na Camp Calma. Mụtakwuo banyere ohere a pụrụ iche.',
    learnMore: 'Mụtakwuo',
    activeProjects: 'Ọrụ Anyị',
    noActiveProjects: 'Enweghị ọrụ na-arụ ọrụ ugbu a.',
    fundingGoal: 'Ebumnuche Ego:',
    alreadyFunded: 'E nyela Ego:',
    deadline: 'Oge Njedebe:',
    progress: 'Ọganihu',
    active: 'na-arụ ọrụ',
    footer: 'E nwere ikike niile.',
    learnMoreAbout: 'Mụtakwuo banyere Sankofa Living & Learning na Sankofa Social digital ecosystem:',
    campCalmaProject: 'Ọrụ Camp Calma',
    sankofaLearning: 'Sankofa Living & Learning',
    sankofaSocial: 'Sankofa Social',
    sankofaSocialDesc: 'Njikọ niile dị mkpa maka digital ecosystem Sankofa Social n\'otu nlele dị nkenke.',
    selectLanguage: 'Họrọ Asụsụ Gị',
    selectLanguageDesc: 'Biko họrọ asụsụ ị masịrị iji gaa n\'ihu',
  },
}

export default function HomeClient({ projects }: HomeClientProps) {
  const [lang, setLang] = useState<Lang>('en')
  const [showLanguagePopup, setShowLanguagePopup] = useState(false)
  
  useEffect(() => {
    const stored = typeof window !== 'undefined' ? (localStorage.getItem('lang') as Lang | null) : null
    if (stored && ['en', 'de', 'nl', 'pt', 'fr', 'es', 'tw', 'ig'].includes(stored)) {
      setLang(stored)
    } else {
      // Show language popup if no language is stored
      setShowLanguagePopup(true)
    }
  }, [])
  
  const t = (key: string) => (strings[lang] && strings[lang][key]) || strings.en[key] || key
  
  const setLanguage = (l: Lang) => {
    setLang(l)
    if (typeof window !== 'undefined') localStorage.setItem('lang', l)
    setShowLanguagePopup(false)
  }

  const formatCurrency = (amount: number | null | undefined, currency?: string | null): string => {
    if (amount === null || amount === undefined) return '€0'
    const numAmount = typeof amount === 'number' ? amount : 0
    const curr = currency || 'EUR'
    if (curr === 'EUR') {
      return `€${numAmount.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    }
    return `${curr} ${numAmount.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  // Helper function to get localized project title
  const getProjectTitle = (project: PublicProjectRow): string => {
    // Use German (original) title when language is 'de', otherwise use English
    if (lang === 'de') {
      return project.title
    }
    return project.title_en || project.title
  }

  // Helper function to get localized project description
  const getProjectDescription = (project: PublicProjectRow): string | null => {
    // Use German (original) description when language is 'de', otherwise use English
    if (lang === 'de') {
      return project.description
    }
    return project.description_en || project.description
  }

  const localeMap: Record<Lang, string> = {
    en: 'en-US',
    de: 'de-DE',
    nl: 'nl-NL',
    pt: 'pt-PT',
    fr: 'fr-FR',
    es: 'es-ES',
    tw: 'en-US',
    ig: 'en-US',
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Language Selection Popup */}
      {showLanguagePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">{t('selectLanguage')}</h2>
            <p className="text-gray-600 mb-6 text-center">{t('selectLanguageDesc')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(['en', 'de', 'nl', 'pt', 'fr', 'es', 'tw', 'ig'] as Lang[]).map((language) => (
                <button
                  key={language}
                  onClick={() => setLanguage(language)}
                  className="px-6 py-4 border-2 border-gray-300 rounded-lg text-lg font-medium text-gray-700 bg-white hover:bg-blue-50 hover:border-blue-500 transition-all"
                >
                  {language.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('mainTitle')}</h1>
              <p className="mt-2 text-lg text-gray-600">{t('mainSubtitle')}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Login button is hidden but not deleted */}
              <Link
                href="/auth/login"
                className="hidden"
                style={{ display: 'none' }}
              >
                {t('login')}
              </Link>
              <select
                aria-label="Language"
                value={lang}
                onChange={(e) => setLanguage(e.target.value as Lang)}
                className="border border-gray-300 rounded-md px-2 py-2 text-gray-700 bg-white text-sm font-medium"
              >
                <option value="en">EN</option>
                <option value="de">DE</option>
                <option value="nl">NL</option>
                <option value="pt">PT</option>
                <option value="fr">FR</option>
                <option value="es">ES</option>
                <option value="tw">TW</option>
                <option value="ig">IG</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Donation and Raffle Section */}
        <div className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Donation Card */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg p-8 border-2 border-green-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('donateNow')}</h2>
                <p className="text-gray-700 mb-4">
                  {t('donateNowDesc')}
                </p>
                <a
                  href="https://donate.sankofa-ngo.org/b/00w4gAdqv0UA76icZFgfu0b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-md transition-colors"
                >
                  {t('donate20')}
                </a>
              </div>
            </div>
          </div>

          {/* Raffle Card */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-lg p-8 border-2 border-orange-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('raffleTitle')}</h2>
                <p className="text-gray-700 mb-2">
                  <strong>{t('rafflePrize')}</strong>
                </p>
                <p className="text-gray-700 mb-4">
                  {t('raffleDesc')}
                </p>
                <a
                  href="https://ticket.sankofa-ngo.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 shadow-md transition-colors"
                >
                  {t('learnMore')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Information Section - Sankofa Resources */}
        <div className="mb-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 border-2 border-blue-200">
          <p className="text-lg text-gray-800 mb-6 font-medium">{t('learnMoreAbout')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Camp Calma Project */}
            <a
              href="https://campcalma.sankofa-ngo.org"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border-2 border-transparent hover:border-green-400"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-3xl">🏕️</span>
                <h3 className="text-xl font-bold text-gray-900">{t('campCalmaProject')}</h3>
              </div>
              <p className="text-sm text-gray-600 break-words">https://campcalma.sankofa-ngo.org</p>
            </a>

            {/* Sankofa Living & Learning */}
            <a
              href="https://www.sankofa-ngo.org"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border-2 border-transparent hover:border-blue-400"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-3xl">🌍</span>
                <h3 className="text-xl font-bold text-gray-900">{t('sankofaLearning')}</h3>
              </div>
              <p className="text-sm text-gray-600 break-words">https://www.sankofa-ngo.org</p>
            </a>

            {/* Sankofa Social */}
            <a
              href="https://overview.sankofa-ngo.org"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border-2 border-transparent hover:border-purple-400"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-3xl">📱</span>
                <h3 className="text-xl font-bold text-gray-900">{t('sankofaSocial')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2 break-words">https://overview.sankofa-ngo.org</p>
              <p className="text-xs text-gray-500 italic">{t('sankofaSocialDesc')}</p>
            </a>
          </div>
        </div>

        {/* Active Projects Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('activeProjects')}</h2>
          
          {projects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600">{t('noActiveProjects')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => {
                const progressPercentage = project.goal_amount && project.goal_amount > 0
                  ? Math.min(Math.round(((project.raised_eur || 0) / project.goal_amount) * 100), 100)
                  : 0

                return (
                  <div key={project.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                    {/* Project Image */}
                    <div className="w-full h-48 overflow-hidden relative bg-gray-100">
                      <SafeImage
                        src={project.image_url}
                        alt={getProjectTitle(project)}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{getProjectTitle(project)}</h3>
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex-shrink-0 ml-2">
                          {t('active')}
                        </span>
                      </div>

                      {project.category && (
                        <div className="mb-3">
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                            {project.category}
                          </span>
                        </div>
                      )}

                      {getProjectDescription(project) && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                          {getProjectDescription(project)}
                        </p>
                      )}

                      {/* Financing Information */}
                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('fundingGoal')}</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(project.goal_amount, project.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('alreadyFunded')}</span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(project.raised_eur)}
                          </span>
                        </div>
                        {project.deadline && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t('deadline')}</span>
                            <span className="font-medium text-gray-900">
                              {new Date(project.deadline).toLocaleDateString(localeMap[lang])}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">{t('progress')}</span>
                          <span className="text-sm font-bold text-blue-600">{progressPercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-600 text-sm">
            © {new Date().getFullYear()} {t('mainTitle')}. {t('footer')}
          </p>
        </div>
      </footer>
    </div>
  )
}
